import { useCallback, useEffect, useState } from 'react';
import { api, ApiError } from '../lib/api';

type RT = 'TEXT' | 'STRUCTURED_TEXT' | 'IMAGE_GENERATION' | 'IMAGE_EDIT' | 'VIDEO_GENERATION' | 'TTS' | 'EMBEDDING';

const TYPES: { key: RT; label: string }[] = [
  { key: 'TEXT', label: 'Text' },
  { key: 'STRUCTURED_TEXT', label: 'Structured text' },
  { key: 'IMAGE_GENERATION', label: 'Image' },
  { key: 'IMAGE_EDIT', label: 'Image edit' },
  { key: 'VIDEO_GENERATION', label: 'Video' },
  { key: 'TTS', label: 'TTS' },
  { key: 'EMBEDDING', label: 'Embedding' },
];

const PRESETS = ['BALANCED', 'CHEAP', 'QUALITY', 'PREMIUM', 'LOCAL_FIRST', 'OPENROUTER_FIRST', 'WORKERS_AI_FIRST'];

interface ChainRow { provider: string; model?: string; local?: boolean }

export function AiRoutingPage() {
  const [chains, setChains] = useState<Record<RT, ChainRow[]>>({} as Record<RT, ChainRow[]>);
  const [overrides, setOverrides] = useState<{ scope: string; scopeKey: string; preset: string | null }[]>([]);
  const [brands, setBrands] = useState<{ id: string; name: string }[]>([]);
  const [brandSel, setBrandSel] = useState('');
  const [trace, setTrace] = useState<{ providerKey: string; modelId: string | null; type: string; estimatedCost: number | null; createdAt: string; status: string; metadata: { fallback?: boolean; reason?: string | null; attempts?: number } }[]>([]);
  const [presetSel, setPresetSel] = useState('OPENROUTER_FIRST');
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const r = await api.aiRouting.get();
    const c = r.chains as unknown as Record<RT, ChainRow[]>;
    const withDefaults: Record<RT, ChainRow[]> = {} as Record<RT, ChainRow[]>;
    for (const t of TYPES) withDefaults[t.key] = c[t.key] ?? [];
    setChains(withDefaults);
    setOverrides(r.overrides);
    api.brands.list().then((b) => setBrands(b.map((x) => ({ id: x.id, name: x.name })))).catch(() => undefined);
    api.generations(30).then((g) => setTrace(g as never)).catch(() => undefined);
  }, []);

  useEffect(() => { load().catch(() => undefined); }, [load]);

  const save = async () => {
    setBusy(true);
    setMessage(null);
    try {
      await api.aiRouting.put({ scope: 'GLOBAL', scopeKey: 'global', chain: chains });
      setMessage('Routing zapisany.');
    } catch (e) {
      setMessage(e instanceof ApiError ? e.message : 'Błąd zapisu.');
    } finally { setBusy(false); }
  };

  const move = async (t: RT, idx: number, dir: -1 | 1) => {
    const arr = [...chains[t]];
    const j = idx + dir;
    if (idx < 0 || j < 0 || j >= arr.length) return;
    const a = arr[idx];
    const b = arr[j];
    if (!a || !b) return;
    arr[idx] = b;
    arr[j] = a;
    setChains({ ...chains, [t]: arr });
  };

  const applyBrandPreset = async () => {
    if (!brandSel) return;
    setBusy(true);
    setMessage(null);
    try {
      await api.aiRouting.put({ scope: 'BRAND', scopeKey: brandSel, preset: presetSel });
      setMessage(`Brand ${brandSel}: preset ${presetSel}.`);
      await load();
    } catch (e) {
      setMessage(e instanceof ApiError ? e.message : 'Błąd zapisu brandu.');
    } finally { setBusy(false); }
  };

  const removeBrandOverride = async (key: string) => {
    try { await api.aiRouting.remove('BRAND', key); await load(); } catch { /* noop */ }
  };

  return (
    <div>
      <h2>AI Routing</h2>
      <p className="muted">GLOBAL (macierz) + brand override. Zmień kolejność Move Up/Down, potem Save.</p>
      {message && <div className="alert" style={{ marginBottom: 12 }}>{message}</div>}

      <div className="card">
        <div className="card__header">Routing matrix (GLOBAL)</div>
        <table>
          <thead><tr><th>Type</th><th>Chain</th><th /></tr></thead>
          <tbody>
            {TYPES.map((t) => (
              <tr key={t.key}>
                <td><strong>{t.label}</strong></td>
                <td>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {chains[t.key]?.map((s, i) => (
                      <span key={`${t.key}-${s.provider}-${i}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: 'var(--surface)', padding: '3px 8px', borderRadius: 6 }}>
                        {s.provider}{s.local ? ' (local)' : ''}{s.model ? ` · ${s.model}` : ''}
                        {i === 0 && <span style={{ color: 'var(--accent)' }}>★</span>}
                      </span>
                    ))}
                  </div>
                </td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  <span />
                  {chains[t.key]?.map((_, i) => (
                    <span key={i}>
                      <button className="btn btn--sm" onClick={() => move(t.key, i, -1)} disabled={i === 0 || busy}>↑</button>
                      <button className="btn btn--sm" onClick={() => move(t.key, i, 1)} disabled={i === (chains[t.key]?.length ?? 1) - 1 || busy}>↓</button>
                    </span>
                  ))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{ marginTop: 10 }}>
          <button className="btn btn--primary" onClick={save} disabled={busy}>Save GLOBAL routing</button>
        </div>
      </div>

      <div className="card" style={{ marginTop: 16 }}>
        <div className="card__header">Routing trace (ostatnie generacje)</div>
        <table>
          <thead><tr><th>When</th><th>Provider</th><th>Model</th><th>Type</th><th>Status</th><th>Fallback</th><th>Reason</th><th>Cost</th></tr></thead>
          <tbody>
            {trace.map((g, i) => (
              <tr key={i}>
                <td className="muted" style={{ fontSize: 12 }}>{g.createdAt?.slice(0, 16)}</td>
                <td>{g.providerKey}</td><td>{g.modelId ?? '—'}</td><td>{g.type}</td>
                <td>{g.status}</td>
                <td>{g.metadata?.fallback ? '⚠ fallback' : 'primary'}</td>
                <td>{g.metadata?.reason ?? '—'}</td>
                <td>{g.estimatedCost != null ? `$${g.estimatedCost.toFixed(4)}` : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="card" style={{ marginTop: 16 }}>
        <div className="card__header">Brand override</div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <select value={brandSel} onChange={(e) => setBrandSel(e.target.value)}>
            <option value="">— brand —</option>
            {brands.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
          <select value={presetSel} onChange={(e) => setPresetSel(e.target.value)}>
            {PRESETS.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
          <button className="btn btn--primary" onClick={applyBrandPreset} disabled={busy}>Apply</button>
        </div>
        {overrides.length > 0 && (
          <ul style={{ marginTop: 10 }}>
            {overrides.map((o) => (
              <li key={`${o.scope}:${o.scopeKey}`}>
                {o.scope} {o.scopeKey} — {o.preset ?? 'custom chain'}
                <button className="btn btn--sm" style={{ marginLeft: 8 }} onClick={() => removeBrandOverride(o.scopeKey)}>usuń</button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}